import React from 'react';
import { motion } from 'framer-motion';
import Footer from './Footer';

const ChatReplyHelper = () => {
  return (
    <div className="about-page policy-page chat-reply-helper-page">
      <div className="about-inner">

        <motion.section
          className="about-intro-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="about-main-title">CHAT REPLY HELPER</h1>
          <p className="about-main-subtitle">
            How Panda can draft a chat reply for you, only when you ask.
          </p>
        </motion.section>

        <hr className="section-divider" />

        <motion.section
          className="policy-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="policy-article">
            <p>
              Chat Reply Helper is a feature of <strong>Panda</strong> (package <code>com.blurr.voice</code>), an
              Android accessibility app from Steepwind Labs. Panda is declared to Google Play as an accessibility tool.
            </p>

            <h2>What it does</h2>
            <p>
              In chat apps, when your keyboard opens, Panda shows one small button above the text box:{' '}
              <strong>&ldquo;Draft reply with Panda&rdquo;</strong>. Tap it, and Panda writes a short reply into your
              text box. You read it, change it, and send it yourself.
            </p>
            <p>
              It works in WhatsApp, WhatsApp Business, Instagram, Telegram, Messenger, Signal, Google Messages, Slack, Discord, Microsoft Teams, Google Chat, X, and LinkedIn.
              It never appears in other apps, and never on a password field.
            </p>

            <h2>Who it is for</h2>
            <p>Typing a reply is slow or tiring for many people, including people who:</p>
            <ul>
              <li>have motor impairments, tremor, or limited dexterity</li>
              <li>have low vision</li>
              <li>use a screen reader</li>
              <li>have dyslexia, or find it hard to put a reply into words</li>
            </ul>
            <p>Chat Reply Helper turns that into one tap, and you still decide what gets sent.</p>

            <h2>It writes as you</h2>
            <p>
              The draft is always <strong>your</strong> next message to the other person. Panda never writes a
              message pretending to be them.
            </p>

            <h2>Nothing is read until you tap</h2>
            <p>
              The button appearing does not read your messages and sends nothing anywhere. Panda only notes the chat
              name on your phone, so the button shows at most once per chat.
            </p>

            <h2>What Panda reads after you tap</h2>
            <ul>
              <li>
                The text of the last few messages on screen (up to 8), marked as yours or theirs by which side of the
                screen they are on, and the chat&apos;s name. Panda reads this with the Android Accessibility Service.
              </li>
              <li>
                If an app hides its text from accessibility, Panda instead takes one picture of only the message
                area. The picture leaves out the status bar, notifications, the top bar with the contact&apos;s photo,
                and your keyboard. It is not saved on your phone.
              </li>
              <li>
                On X, Panda always uses that picture, because X does not reliably share the text of the post you are
                replying to. Keep the post visible above the keyboard when you tap the button.
              </li>
            </ul>

            <h2>What is sent, and to whom</h2>
            <p>
              Only after you tap, those messages (or that picture) are sent over an encrypted connection to
              Panda&apos;s server. The server asks an AI model to write the draft and sends it back to your phone. When you ask for a different reply or a change, Panda also sends the text
              that is in your text box at that moment.
            </p>
            <p>
              On the free plan, your data is used to train our models. On Pro and Bring Your Own Key, your data is
              used only if you choose that. We do not sell your data. Read our full{' '}
              <a href="/privacy-policy" className="policy-link">
                privacy policy
              </a>
              .
            </p>

            <h2>Not quite right? Change it in one tap</h2>
            <p>
              After the draft is in your text box, two small buttons appear above it. One writes a different
              reply. The other offers quick changes: warmer, neutral, sympathetic, shorter, or longer. Each change
              rewrites what is in your text box now, so your own edits are kept.
            </p>

            <h2>Nothing is sent for you</h2>
            <p>
              Panda only puts the draft in your text box. It never presses Send. You always read, edit, and send the
              message yourself.
            </p>

            <h2>You stay in control</h2>
            <p>Chat Reply Helper is on by default for Panda users, and it goes away easily:</p>
            <ul>
              <li><strong>&times; on the button:</strong> hides it in that chat.</li>
              <li>
                <strong>It turns itself off:</strong> if you close it 3 times in a row, or ignore it 10 times in a
                row, Panda turns the feature off on its own.
              </li>
            </ul>
            <p>
              After your first draft, a small notice says <strong>&ldquo;Reply drafted by Panda&rdquo;</strong>. It has
              three options:
            </p>
            <ul>
              <li><strong>Keep:</strong> leave the feature on.</li>
              <li><strong>Disable:</strong> turn the feature off straight away.</li>
              <li><strong>Know more:</strong> open this page.</li>
            </ul>
            <p>
              To turn it back on, open Panda&apos;s <strong>Settings</strong> and switch on{' '}
              <strong>Reply suggestions when keyboard opens</strong>. You can also turn off Panda&apos;s accessibility
              service at any time in Android <strong>Settings &rarr; Accessibility</strong>.
            </p>

            <h2>Not affiliated</h2>
            <p>
              Panda and Steepwind Labs are not affiliated with, endorsed by, or sponsored by WhatsApp, Instagram, Meta,
              Telegram, Signal, Google, Slack, Discord, Microsoft, X, or LinkedIn. Their names are used only to say which apps this feature works with.
            </p>

            <h2>Questions</h2>

            <h3>Does Panda send messages for me?</h3>
            <p>No. Panda puts a draft in your text box. You decide whether to send it.</p>

            <h3>Does Panda read my chats in the background?</h3>
            <p>
              No. Panda reads the messages on screen only after you tap &ldquo;Draft reply with Panda&rdquo;, and only
              in that chat.
            </p>

            <h3>What is sent to Panda&apos;s server?</h3>
            <p>
              After you tap: the last few messages on screen (up to 8) and the chat&apos;s name, or a picture of only
              the message area when an app hides its text. When you ask for a change, also the text in your text box.
            </p>

            <h3>How do I turn it off?</h3>
            <p>
              Tap <strong>Disable</strong> on the &ldquo;Reply drafted by Panda&rdquo; notice, or tap &times; on the
              button three times. Or turn off Panda&apos;s accessibility service in Android{' '}
              <strong>Settings &rarr; Accessibility</strong>.
            </p>

            <h2>Contact</h2>
            <p>
              Questions or feedback:{' '}
              <a href="mailto:support@heypanda.org" className="policy-link">
                support@heypanda.org
              </a>
              .
            </p>
          </div>
        </motion.section>
      </div>

      <Footer />

      <style jsx>{`
        .policy-page {
          background: transparent;
        }

        .policy-article {
          max-width: 800px;
          margin: 0 auto;
        }

        .policy-article h2 {
          font-size: 1.5rem;
          color: var(--primary-color);
          margin-top: 40px;
          margin-bottom: 20px;
          padding-bottom: 8px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .policy-article h3 {
          font-size: 1.15rem;
          color: var(--text-primary);
          margin-top: 24px;
          margin-bottom: 12px;
        }

        .policy-article p {
          font-size: 1.05rem;
          line-height: 1.8;
          color: var(--text-secondary);
          margin-bottom: 16px;
        }

        .policy-article ul {
          margin-bottom: 24px;
          padding-left: 24px;
        }

        .policy-article li {
          font-size: 1.05rem;
          line-height: 1.8;
          color: var(--text-secondary);
          margin-bottom: 8px;
        }

        .policy-article strong {
          color: var(--text-primary);
        }

        .policy-article code {
          background: rgba(255, 255, 255, 0.08);
          padding: 2px 6px;
          border-radius: 4px;
          font-family: monospace;
          color: var(--primary-color);
        }

        .chat-reply-helper-page .policy-link {
          color: var(--primary-color);
          text-decoration: underline;
          text-underline-offset: 3px;
        }

        .chat-reply-helper-page .policy-link:hover,
        .chat-reply-helper-page .policy-link:focus-visible {
          color: var(--text-primary);
        }

        .chat-reply-helper-page .policy-link:focus-visible {
          outline: 2px solid var(--primary-color);
          outline-offset: 2px;
        }

        @media (max-width: 768px) {
          .policy-article h2 {
            font-size: 1.25rem;
          }
          .policy-article h3 {
            font-size: 1.05rem;
          }
          .policy-article p,
          .policy-article li {
            font-size: 0.95rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ChatReplyHelper;
